import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Mail, Check, CalendarClock } from 'lucide-react';
import { Lead } from '../data/mockLeads';
import { Button } from './Button';
export type ActionMode = 'call' | 'email' | 'schedule';
// Format an ISO date string into the local yyyy-MM-ddTHH:mm value a
// datetime-local input expects.
const toDateTimeInputValue = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
interface LeadActionModalProps {
  lead: Lead | null;
  mode: ActionMode;
  open: boolean;
  onClose: () => void;
  onSubmit: (leadId: string, mode: ActionMode, payload: Record<string, string>) => void;
}
const CALL_OUTCOMES = ['Spoke with lead', 'Left voicemail', 'No answer', 'Wrong number'];
export function LeadActionModal({
  lead,
  mode,
  open,
  onClose,
  onSubmit
}: LeadActionModalProps) {
  const [outcome, setOutcome] = useState(CALL_OUTCOMES[0]);
  const [notes, setNotes] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  // Reset/seed form whenever a new lead/mode is opened
  useEffect(() => {
    if (open && lead) {
      setOutcome(CALL_OUTCOMES[0]);
      setNotes('');
      setSubject(`Following up on your ${lead.loanPurpose.toLowerCase()} loan`);
      setBody(`Hi ${lead.name.split(' ')[0]},\n\nI wanted to follow up regarding your ${lead.loanPurpose.toLowerCase()} loan. Do you have a few minutes this week to connect?\n\nBest regards,\nYour Loan Officer`);
      setFollowUpDate(toDateTimeInputValue(lead.nextFollowUp));
    }
  }, [open, lead, mode]);
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!lead) return null;
  const isCall = mode === 'call';
  const isSchedule = mode === 'schedule';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Record<string, string> = isCall ? {
      outcome,
      notes
    } : isSchedule ? {
      nextFollowUp: new Date(followUpDate).toISOString()
    } : {
      subject,
      body
    };
    onSubmit(lead.id, mode, payload);
  };
  return createPortal(<AnimatePresence data-id="element-47" data-mp="1m66tc3se238b">
      {open && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-48" data-mp="1m311igsdkn8w">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-49" data-mp="187paajdfhewq" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="lead-action-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.15
      }} className="relative w-full max-w-lg bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-50" data-mp="13hd258r913hx">
            <div className="flex items-start justify-between p-6 border-b border-slate-200" data-id="element-51" data-mp="1v5wo36pkpng0">
              <div className="flex items-center gap-3" data-id="element-52" data-mp="1r4uogorqv9di">
                <div className={`p-2.5 rounded-lg ${isCall ? 'bg-blue-50 text-blue-600' : isSchedule ? 'bg-emerald-50 text-emerald-600' : 'bg-purple-50 text-purple-600'}`} data-id="element-53" data-mp="3kod2epgjri4j">
                  {isCall ? <Phone size={20} data-id="element-54" data-mp="3uhk7083gqu3k" /> : isSchedule ? <CalendarClock size={20} data-id="element-55" data-mp="38dbixltdmsj" /> : <Mail size={20} data-id="element-56" data-mp="31rkbv0yrkvdw" />}
                </div>
                <div data-id="element-57" data-mp="36pdjzzjmnzgt">
                  <h2 id="lead-action-title" className="text-lg font-semibold text-slate-900" data-id="element-58" data-mp="36qok9pdv6p36">
                    {isCall ? 'Log Call' : isSchedule ? 'Set Next Follow-up' : 'Send Email'}
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-59" data-mp="v02paik5jmq1">
                    {lead.name} ·{' '}
                    {isCall || isSchedule ? lead.phone : lead.email}
                  </p>
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors" data-id="element-60" data-mp="2bg9wllf2g65x">
                <X size={20} data-id="element-61" data-mp="21ow0zoq8jqi2" />
              </button>
            </div>

            <form onSubmit={handleSubmit} data-id="element-62" data-mp="1wt6ypdmtzvi2">
              <div className="p-6 space-y-4" data-id="element-63" data-mp="1rsizw5sd2ada">
                {isCall ? <>
                    <div data-id="element-64" data-mp="1nqxl2ew9nm0g">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-65" data-mp="1nq5ujihj0eh1">
                        Call outcome
                      </label>
                      <select value={outcome} onChange={e => setOutcome(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-66" data-mp="1iu7au8757kmy">
                        {CALL_OUTCOMES.map(o => <option key={o} value={o} data-id="element-67" data-mp="4xnskbjt82ig">
                            {o}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-68" data-mp="3s6j1u4tg71fw">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-69" data-mp="jsk879ty7s5k">
                        Notes
                      </label>
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder="What did you discuss?" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-70" data-mp="ntw1pqfy0xj1" />
                    </div>
                  </> : isSchedule ? <div data-id="element-71" data-mp="2s2tecjo99208">
                    <label htmlFor="next-follow-up" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-72" data-mp="2n1vzafyjko2y">
                      Next follow-up date &amp; time
                    </label>
                    <input id="next-follow-up" type="datetime-local" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} min={toDateTimeInputValue(new Date().toISOString())} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-id="element-73" data-mp="2w33kpxfylufm" />
                    <p className="mt-2 text-xs text-slate-500" data-id="element-74" data-mp="1pknj1fj5hfka">
                      Schedule when {lead.name.split(' ')[0]} should next be
                      contacted.
                    </p>
                  </div> : <>
                    <div data-id="element-75" data-mp="14257oe7w3d9y">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-76" data-mp="143f8vca1h1kt">
                        Subject
                      </label>
                      <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-77" data-mp="1e0te0fqii3xt" />
                    </div>
                    <div data-id="element-78" data-mp="1wrkstwt9qmjl">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-79" data-mp="1wru8orklndbk">
                        Message
                      </label>
                      <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-80" data-mp="21okqbjc1ygz3" />
                    </div>
                  </>}
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50" data-id="element-81" data-mp="3cbblrct50en4">
                <Button type="button" variant="outline" onClick={onClose} data-id="element-82" data-mp="drl9c9d7xbvn">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isSchedule && !followUpDate} icon={isCall ? <Check size={16} data-id="element-84" data-mp="3v7bh8xlbq0bg" /> : isSchedule ? <CalendarClock size={16} data-id="element-85" data-mp="wkz0029wew5g" /> : <Mail size={16} data-id="element-86" data-mp="rrmkqir401pd" />} data-id="element-83" data-mp="8ulc8nycjjgo">
                  {isCall ? 'Save Call' : isSchedule ? 'Set Follow-up' : 'Send Email'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>, document.body);
}